export type OtMethod = 'weekly' | 'daily' | 'both' | 'none'
export type PayPeriodType = 'weekly' | 'biweekly'

/** Operator ids that can be toggled in Operation Config (matches OPERATORS catalog). */
export const CONFIGURABLE_OPERATOR_IDS = [
  'eta',
  'nearby',
  'air-miles',
  'contains',
  'local-license-check',
  'weather-forecast',
  'forecast',
  'time',
  'cert-check',
] as const

export type ConfigurableOperatorId = (typeof CONFIGURABLE_OPERATOR_IDS)[number]

export type ControlCenterOperationConfig = {
  otMethod: OtMethod
  weeklyThresholdHours: number
  dailyThresholdHours: number
  /** 0=Sunday … 6=Saturday */
  weekStartsOn: number
  payPeriodType: PayPeriodType
  biweeklyAnchorDate: string | null
  /**
   * Payday weekday (0=Sunday … 6=Saturday). Payday is the first occurrence
   * of this weekday strictly after the pay period ends. Default Friday.
   */
  payDateWeekday: number
  /**
   * Per-operator enable flags. Missing keys default to enabled.
   * Explicit `false` disables the operator across Control Center.
   */
  enabledOperators: Partial<Record<ConfigurableOperatorId, boolean>>
}

export type PayrollConfig = Omit<ControlCenterOperationConfig, 'enabledOperators'>

export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const

/** Overtime is always hours over 40 in a week. Not configurable. */
export const LOCKED_OVERTIME = {
  otMethod: 'weekly' as const,
  weeklyThresholdHours: 40,
}

export const DEFAULT_OPERATION_CONFIG: ControlCenterOperationConfig = {
  otMethod: LOCKED_OVERTIME.otMethod,
  weeklyThresholdHours: LOCKED_OVERTIME.weeklyThresholdHours,
  dailyThresholdHours: 8,
  weekStartsOn: 0,
  payPeriodType: 'weekly',
  biweeklyAnchorDate: null,
  payDateWeekday: 5,
  enabledOperators: {},
}

export function isOperatorEnabledInConfig(
  config: ControlCenterOperationConfig,
  operatorId: string,
): boolean {
  const flag = config.enabledOperators?.[operatorId as ConfigurableOperatorId]
  return flag !== false
}

export function normalizeOperationConfig(
  raw: unknown,
): ControlCenterOperationConfig {
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {}
  const otMethod = LOCKED_OVERTIME.otMethod
  const weeklyThresholdHours = LOCKED_OVERTIME.weeklyThresholdHours
  const dailyThresholdHours = clampHours(
    src.dailyThresholdHours,
    DEFAULT_OPERATION_CONFIG.dailyThresholdHours,
    1,
    24,
  )

  let weekStartsOn = Number(src.weekStartsOn)
  if (!Number.isInteger(weekStartsOn) || weekStartsOn < 0 || weekStartsOn > 6) {
    weekStartsOn = DEFAULT_OPERATION_CONFIG.weekStartsOn
  }

  const payPeriodType = ['weekly', 'biweekly'].includes(String(src.payPeriodType))
    ? (String(src.payPeriodType) as PayPeriodType)
    : DEFAULT_OPERATION_CONFIG.payPeriodType

  let biweeklyAnchorDate: string | null = null
  if (src.biweeklyAnchorDate != null && String(src.biweeklyAnchorDate).trim()) {
    const ymd = String(src.biweeklyAnchorDate).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) biweeklyAnchorDate = ymd
  }

  let payDateWeekday = Number(src.payDateWeekday)
  if (!Number.isInteger(payDateWeekday) || payDateWeekday < 0 || payDateWeekday > 6) {
    payDateWeekday = DEFAULT_OPERATION_CONFIG.payDateWeekday
  }

  const enabledOperators: Partial<Record<ConfigurableOperatorId, boolean>> = {}
  const rawEnabled =
    src.enabledOperators && typeof src.enabledOperators === 'object' && !Array.isArray(src.enabledOperators)
      ? (src.enabledOperators as Record<string, unknown>)
      : {}
  for (const id of CONFIGURABLE_OPERATOR_IDS) {
    if (rawEnabled[id] === false) enabledOperators[id] = false
    else if (rawEnabled[id] === true) enabledOperators[id] = true
  }

  return {
    otMethod,
    weeklyThresholdHours,
    dailyThresholdHours,
    weekStartsOn,
    payPeriodType,
    biweeklyAnchorDate,
    payDateWeekday,
    enabledOperators,
  }
}

function clampHours(value: unknown, fallback: number, min: number, max: number): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return fallback
  return Math.min(max, Math.max(min, Math.round(n * 100) / 100))
}

export type TimeRangePresetId =
  | 'today'
  | 'this_week'
  | 'this_pay_period'
  | 'this_month'
  | 'this_quarter'
  | 'year_to_date'
  | 'custom'

export const TIME_RANGE_PRESET_OPTIONS: Array<{ id: TimeRangePresetId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This week' },
  { id: 'this_pay_period', label: 'This pay period' },
  { id: 'this_month', label: 'This month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'year_to_date', label: 'Year to date' },
  { id: 'custom', label: 'Custom' },
]

export function toLocalYmd(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

/** Whole local calendar days from `from` to `to`. Avoids DST off-by-one from ms/86400000. */
function calendarDaysBetween(from: Date, to: Date): number {
  const a = startOfLocalDay(from)
  const b = startOfLocalDay(to)
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())
  return Math.round((utcB - utcA) / (24 * 60 * 60 * 1000))
}

function weekStartLocal(d: Date, weekStartsOn: number): Date {
  const day = startOfLocalDay(d)
  const start = ((weekStartsOn % 7) + 7) % 7
  const diff = (day.getDay() - start + 7) % 7
  day.setDate(day.getDate() - diff)
  return day
}

function parseYmdLocal(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd)
  if (!m) return null
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Number.isNaN(d.getTime()) ? null : d
}

/** Current pay period containing `today` based on operation config. */
export function payPeriodBounds(
  config: ControlCenterOperationConfig,
  today = new Date(),
): { start: Date; end: Date } {
  const weekStartsOn = config.weekStartsOn
  if (config.payPeriodType === 'biweekly') {
    const anchor =
      parseYmdLocal(config.biweeklyAnchorDate || '') ||
      weekStartLocal(today, weekStartsOn)
    const todayStart = startOfLocalDay(today)
    const anchorStart = startOfLocalDay(anchor)
    const days = calendarDaysBetween(anchorStart, todayStart)
    const periodIndex = Math.floor(days / 14)
    const start = new Date(anchorStart)
    start.setDate(start.getDate() + periodIndex * 14)
    const end = new Date(start)
    end.setDate(end.getDate() + 13)
    // Full pay period (including future days), not capped to today.
    return { start, end }
  }
  const start = weekStartLocal(today, weekStartsOn)
  const end = new Date(start)
  end.setDate(end.getDate() + 6)
  return { start, end }
}

/**
 * Pay periods after the one containing `today`, chronological.
 */
export function nextPayPeriods(
  config: ControlCenterOperationConfig,
  today = new Date(),
  count = 52,
): Array<{ start: Date; end: Date }> {
  const n = Math.min(104, Math.max(0, Math.trunc(Number(count) || 0)))
  const current = payPeriodBounds(config, today)
  let cursor = startOfLocalDay(current.end)
  cursor.setDate(cursor.getDate() + 1)
  const periods: Array<{ start: Date; end: Date }> = []
  for (let i = 0; i < n; i++) {
    const bounds = payPeriodBounds(config, cursor)
    periods.push(bounds)
    cursor = startOfLocalDay(bounds.end)
    cursor.setDate(cursor.getDate() + 1)
  }
  return periods
}

export type UpcomingPayPeriod = { start: Date; end: Date; payDate: Date }

/**
 * Earliest period cursor whose payday is still today or later.
 * Payday falls after the period ends, so the previous period can be the next
 * paycheck while today already sits in a later period.
 */
function unpaidPayPeriodCursor(config: ControlCenterOperationConfig, todayStart: Date): Date {
  let cursor = todayStart
  for (let i = 0; i < 8; i++) {
    const bounds = payPeriodBounds(config, cursor)
    const dayBefore = startOfLocalDay(bounds.start)
    dayBefore.setDate(dayBefore.getDate() - 1)
    const prev = payPeriodBounds(config, dayBefore)
    if (toLocalYmd(prev.start) === toLocalYmd(bounds.start)) break
    const prevPay = payDateForPeriodEnd(prev.end, config.payDateWeekday)
    if (startOfLocalDay(prevPay).getTime() < todayStart.getTime()) break
    cursor = dayBefore
  }
  return cursor
}

/**
 * Pay periods whose payday is today or later.
 * Includes a prior period when that paycheck has not happened yet.
 */
export function upcomingPayPeriods(
  config: ControlCenterOperationConfig,
  today = new Date(),
  count = 52,
): UpcomingPayPeriod[] {
  const n = Math.min(104, Math.max(0, Math.trunc(Number(count) || 0)))
  const todayStart = startOfLocalDay(today)
  let cursor = unpaidPayPeriodCursor(config, todayStart)
  const periods: UpcomingPayPeriod[] = []
  const seen = new Set<string>()
  for (let i = 0; i < 120 && periods.length < n; i++) {
    const bounds = payPeriodBounds(config, cursor)
    const key = toLocalYmd(bounds.start)
    if (seen.has(key)) break
    seen.add(key)
    const payDate = payDateForPeriodEnd(bounds.end, config.payDateWeekday)
    if (startOfLocalDay(payDate).getTime() >= todayStart.getTime()) {
      periods.push({ start: bounds.start, end: bounds.end, payDate })
    }
    cursor = startOfLocalDay(bounds.end)
    cursor.setDate(cursor.getDate() + 1)
  }
  return periods
}

/**
 * Pay periods around `today`: `pastCount` prior periods, the current period,
 * then `futureCount` following periods.
 */
export function nearbyPayPeriods(
  config: ControlCenterOperationConfig,
  today = new Date(),
  pastCount = 6,
  futureCount = 2,
): UpcomingPayPeriod[] {
  const nPast = Math.min(52, Math.max(0, Math.trunc(Number(pastCount) || 0)))
  const nFuture = Math.min(52, Math.max(0, Math.trunc(Number(futureCount) || 0)))
  const weekday = config.payDateWeekday
  const current = payPeriodBounds(config, today)
  const periods: UpcomingPayPeriod[] = [{
    start: current.start,
    end: current.end,
    payDate: payDateForPeriodEnd(current.end, weekday),
  }]

  let cursor = startOfLocalDay(current.start)
  cursor.setDate(cursor.getDate() - 1)
  for (let i = 0; i < nPast; i++) {
    const bounds = payPeriodBounds(config, cursor)
    periods.unshift({
      start: bounds.start,
      end: bounds.end,
      payDate: payDateForPeriodEnd(bounds.end, weekday),
    })
    cursor = startOfLocalDay(bounds.start)
    cursor.setDate(cursor.getDate() - 1)
  }

  cursor = startOfLocalDay(current.end)
  cursor.setDate(cursor.getDate() + 1)
  for (let i = 0; i < nFuture; i++) {
    const bounds = payPeriodBounds(config, cursor)
    periods.push({
      start: bounds.start,
      end: bounds.end,
      payDate: payDateForPeriodEnd(bounds.end, weekday),
    })
    cursor = startOfLocalDay(bounds.end)
    cursor.setDate(cursor.getDate() + 1)
  }

  return periods
}

/**
 * Payday for a period: first occurrence of `payDateWeekday` strictly after period end.
 * Default Friday = the following Friday.
 */
export function payDateForPeriodEnd(periodEnd: Date, payDateWeekday = 5): Date {
  const weekday =
    Number.isInteger(payDateWeekday) && payDateWeekday >= 0 && payDateWeekday <= 6
      ? payDateWeekday
      : DEFAULT_OPERATION_CONFIG.payDateWeekday
  const d = startOfLocalDay(periodEnd)
  d.setDate(d.getDate() + 1)
  const diff = (weekday - d.getDay() + 7) % 7
  d.setDate(d.getDate() + diff)
  return d
}

export type PayDateConfig = Pick<
  ControlCenterOperationConfig,
  'payPeriodType' | 'weekStartsOn' | 'biweeklyAnchorDate' | 'payDateWeekday'
>

function nearbyPayDates(config: ControlCenterOperationConfig, around: Date): Date[] {
  let cursor = startOfLocalDay(around)
  for (let i = 0; i < 3; i++) {
    const bounds = payPeriodBounds(config, cursor)
    cursor = startOfLocalDay(bounds.start)
    cursor.setDate(cursor.getDate() - 1)
  }
  const dates: Date[] = []
  const seen = new Set<string>()
  for (let i = 0; i < 10; i++) {
    const bounds = payPeriodBounds(config, cursor)
    const payDate = startOfLocalDay(payDateForPeriodEnd(bounds.end, config.payDateWeekday))
    const key = toLocalYmd(payDate)
    if (!seen.has(key)) {
      seen.add(key)
      dates.push(payDate)
    }
    cursor = startOfLocalDay(bounds.end)
    cursor.setDate(cursor.getDate() + 1)
  }
  dates.sort((a, b) => a.getTime() - b.getTime())
  return dates
}

/**
 * If `isoDate` is a payday, move to the next one. Otherwise snap to the nearest payday
 * (ties go forward).
 */
export function stepToPayDate(config: PayDateConfig, isoDate: string): string {
  const full = { ...DEFAULT_OPERATION_CONFIG, ...config }
  const parsed = parseYmdLocal(isoDate) || startOfLocalDay(new Date())
  const from = startOfLocalDay(parsed)
  const fromYmd = toLocalYmd(from)
  const dates = nearbyPayDates(full, from)
  if (!dates.length) return fromYmd
  const onPayday = dates.some((d) => toLocalYmd(d) === fromYmd)
  if (onPayday) {
    const next = dates.find((d) => d.getTime() > from.getTime())
    return next ? toLocalYmd(next) : fromYmd
  }
  let best = dates[0]
  let bestDist = Math.abs(calendarDaysBetween(from, best))
  for (const d of dates) {
    const dist = Math.abs(calendarDaysBetween(from, d))
    if (dist < bestDist || (dist === bestDist && d.getTime() > best.getTime())) {
      best = d
      bestDist = dist
    }
  }
  return toLocalYmd(best)
}

export function resolveTimeRangePreset(
  preset: TimeRangePresetId,
  config: ControlCenterOperationConfig,
  today = new Date(),
): { timeStartDate: string; timeEndDate: string } | null {
  if (preset === 'custom') return null
  const end = startOfLocalDay(today)
  let start = startOfLocalDay(today)

  switch (preset) {
    case 'today':
      break
    case 'this_week': {
      start = weekStartLocal(today, config.weekStartsOn)
      break
    }
    case 'this_pay_period': {
      const bounds = payPeriodBounds(config, today)
      start = bounds.start
      return {
        timeStartDate: toLocalYmd(bounds.start),
        timeEndDate: toLocalYmd(bounds.end),
      }
    }
    case 'this_month':
      start = new Date(today.getFullYear(), today.getMonth(), 1)
      break
    case 'this_quarter': {
      const q = Math.floor(today.getMonth() / 3) * 3
      start = new Date(today.getFullYear(), q, 1)
      break
    }
    case 'year_to_date':
      start = new Date(today.getFullYear(), 0, 1)
      break
    default:
      return null
  }

  return {
    timeStartDate: toLocalYmd(start),
    timeEndDate: toLocalYmd(end),
  }
}

/** Infer which preset matches the current dates (or custom). */
export function matchTimeRangePreset(
  startDate: string,
  endDate: string,
  config: ControlCenterOperationConfig,
  today = new Date(),
): TimeRangePresetId {
  for (const opt of TIME_RANGE_PRESET_OPTIONS) {
    if (opt.id === 'custom') continue
    const resolved = resolveTimeRangePreset(opt.id, config, today)
    if (
      resolved &&
      resolved.timeStartDate === startDate &&
      resolved.timeEndDate === endDate
    ) {
      return opt.id
    }
  }
  return 'custom'
}
